import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { LogIn, KeyRound, ArrowLeft, Sparkles, UserPlus } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import GoogleAccountButton from '@/components/GoogleAccountButton';
import { persistCsrfToken } from '@/contexts/AuthContext';

/**
 * QuoteLogin
 * ----------
 * Standalone, minimal login screen shown at `/` when
 * `REACT_APP_QUOTE_MODULE_ENABLED=true`. Isolated from the legacy
 * `Landing` component. Uses the same `/api/auth/login` and
 * `/api/auth/forgot-password` endpoints so backend and DB stay untouched.
 * On success → navigate('/quotes').
 */
export const QuoteLogin = () => {
  const navigate = useNavigate();
  const { login, logout, checkAuth } = useAuth();
  const { t } = useTranslation('quotes');
  const API_URL = process.env.REACT_APP_API_URL;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setLoading(false);
      toast.error(result.error || t('login.loginFailed'));
      return;
    }
    if (result.role === 'superadmin') {
      setLoading(false);
      toast.success(t('login.loginSuccess'));
      navigate('/superadmin');
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/api/quote/setup-status`);
      if (data.eligible === false) {
        await logout();
        toast.error(t('login.legacyBlocked'), { duration: 8000 });
        setLoading(false);
        return;
      }
      toast.success(t('login.loginSuccess'));
      navigate(data.setup_completed ? '/quotes' : '/quote-setup');
    } catch (err) {
      await logout();
      toast.error(t('login.legacyBlocked'), { duration: 8000 });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async (credential) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/auth/google`, { id_token: credential }, { withCredentials: true });
      sessionStorage.setItem('access_token', data.access_token);
      persistCsrfToken(data.csrf_token);
      await checkAuth();
      const result = await axios.get(`${API_URL}/api/quote/setup-status`);
      if (result.data.eligible === false) {
        await logout();
        toast.error(t('login.legacyBlocked'));
        return;
      }
      navigate(result.data.setup_completed ? '/quotes' : '/quote-setup');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === 'registration_required') {
        navigate('/quote-register', { state: { googleCredential: credential, googleProfile: detail.google_profile } });
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || t('login.loginFailed'));
      }
    } finally { setLoading(false); }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      toast.success(t('login.forgotSuccess'));
      setShowForgot(false);
      setForgotEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('login.forgotError'));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#F9F8F6] to-[#EBE9E4] p-4"
      data-testid="quote-login-page"
    >
      <div className="w-full max-w-sm space-y-5">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex items-center gap-3">
            <img
              src="/logo-paydaai-icon.png"
              alt="PaydaAI"
              className="h-14 w-14 sm:h-16 sm:w-16 object-contain drop-shadow-md rounded-2xl"
              data-testid="quote-login-logo"
            />
            <span
              className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A2E]"
              data-testid="quote-login-wordmark"
            >
              PaydaAI
            </span>
          </div>
          <div>
            <p
              className="text-sm font-medium text-[#2563EB] flex items-center justify-center gap-1.5"
              data-testid="quote-login-title"
            >
              <Sparkles className="w-4 h-4" strokeWidth={2} />
              {t('login.title')}
            </p>
            <p
              className="text-xs text-[#595959] mt-1"
              data-testid="quote-login-subtitle"
            >
              {t('login.subtitle')}
            </p>
          </div>
        </div>

        <Card className="border border-[#E5E5E5] shadow-lg" data-testid="quote-login-card">
          <CardContent className="p-5">
            {!showForgot ? (
              <form onSubmit={handleLogin} className="space-y-4" data-testid="quote-login-form">
                <div className="space-y-1.5">
                  <Label className="text-sm">{t('login.email')}</Label>
                  <Input
                    type="email"
                    placeholder={t('login.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="quote-login-email-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">{t('login.password')}</Label>
                    <button
                      type="button"
                      onClick={() => setShowForgot(true)}
                      className="text-xs text-[#2563EB] hover:underline font-medium"
                      data-testid="quote-login-forgot-link"
                    >
                      {t('login.forgotLink')}
                    </button>
                  </div>
                  <Input
                    type="password"
                    placeholder={t('login.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="quote-login-password-input"
                    className="h-11"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 text-white gap-2 bg-[#2563EB] hover:bg-[#1E4ED8]"
                  disabled={loading}
                  data-testid="quote-login-submit"
                >
                  {loading ? t('login.submitting') : (<><LogIn className="w-4 h-4" /> {t('login.submit')}</>)}
                </Button>

                <GoogleAccountButton onCredential={handleGoogle} disabled={loading} />
                <div className="pt-2 border-t border-[#F1F5F9] text-center">
                  <p className="text-xs text-[#595959] mb-2">{t('login.noAccount')}</p>
                  <Link
                    to="/quote-register"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2563EB] hover:underline"
                    data-testid="quote-login-register-link"
                  >
                    <UserPlus className="w-4 h-4" /> {t('login.createAccount')}
                  </Link>
                </div>
              </form>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <button
                    onClick={() => { setShowForgot(false); setForgotEmail(''); }}
                    className="p-1.5 rounded-lg hover:bg-[#F0F0F0] transition"
                    data-testid="quote-login-back-to-login"
                  >
                    <ArrowLeft className="w-5 h-5 text-[#595959]" />
                  </button>
                  <div>
                    <p className="text-sm font-semibold text-[#1A1A2E]">{t('login.forgotTitle')}</p>
                    <p className="text-xs text-[#595959]">{t('login.forgotSubtitle')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <KeyRound className="w-5 h-5 text-amber-600 shrink-0" />
                  <p className="text-xs text-amber-700">
                    {t('login.forgotHint')}
                  </p>
                </div>

                <form onSubmit={handleForgotSubmit} className="space-y-4" data-testid="quote-forgot-form">
                  <div className="space-y-1.5">
                    <Label className="text-sm">{t('login.forgotEmailLabel')}</Label>
                    <Input
                      type="email"
                      placeholder={t('login.emailPlaceholder')}
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      data-testid="quote-forgot-email-input"
                      className="h-11"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white gap-2"
                    disabled={forgotLoading}
                    data-testid="quote-forgot-submit"
                  >
                    {forgotLoading ? t('login.forgotSubmitting') : (<><KeyRound className="w-4 h-4" /> {t('login.forgotSubmit')}</>)}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>

        <div className="rounded-xl border border-[#E5E5E5] bg-white/70 p-4 text-center text-xs text-[#595959] leading-relaxed" data-testid="quote-public-product-info">
          <p>
            PaydaAI is AI-powered quotation software for creating, managing, and exporting professional price quotes and PDFs.
          </p>
          <p className="mt-1">
            PaydaAI does not provide customer payment collection, debt tracking, or appointment booking services.
          </p>
          <div className="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-1">
            <a href="https://paydaai.com/en" className="text-[#2563EB] hover:underline">Product</a>
            <a href="https://paydaai.com/en#pricing" className="text-[#2563EB] hover:underline">Pricing</a>
            <a href="https://paydaai.com/en/terms-of-use" className="text-[#2563EB] hover:underline">Terms</a>
            <a href="https://paydaai.com/en/privacy-policy" className="text-[#2563EB] hover:underline">Privacy</a>
            <a href="https://paydaai.com/en/refund-policy" className="text-[#2563EB] hover:underline">Refunds</a>
          </div>
        </div>

        <p className="text-[10px] text-center text-[#999]" data-testid="quote-login-footer">
          PaydaAI v1.0
        </p>
      </div>
    </div>
  );
};

export default QuoteLogin;
